import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

/**
 * The first-contract prompt (D9). A task bills through a contract only by its
 * own explicit mapping (D8) — creating the project's first contract maps
 * nothing by itself. So the moment the first contract exists on a project that
 * already has unmapped tasks, the user decides: connect every task to it now,
 * or map each task themselves in Planning. Only the FIRST contract asks;
 * later contracts never sweep tasks.
 */
export type FirstContractDialogProps = {
  open: boolean;
  contractName: string;
  unmappedCount: number;
  busy?: boolean;
  onConnectAll: () => void;
  onMapManually: () => void;
};

export const FirstContractDialog = ({
  open,
  contractName,
  unmappedCount,
  busy = false,
  onConnectAll,
  onMapManually,
}: FirstContractDialogProps) => {
  const { t } = useTranslation('pmFinancials');
  return (
    <Dialog open={open} data-automation-id="first-contract-dialog" maxWidth="xs" fullWidth>
      <DialogTitle>{t('firstContractDialog.Title', { name: contractName })}</DialogTitle>
      <DialogContent>
        <Typography variant="bodyRegular" color="text.secondary">
          {t('firstContractDialog.Intro', { name: contractName })}{' '}
          <span data-automation-id="first-contract-unmapped-count">
            {t('firstContractDialog.Unmapped', { count: unmappedCount })}
          </span>{' '}
          {t('firstContractDialog.Rest', { name: contractName })}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onMapManually}
          disabled={busy}
          color="inherit"
          data-automation-id="first-contract-map-manually"
        >
          {t('firstContractDialog.MapManually')}
        </Button>
        <Button
          onClick={onConnectAll}
          disabled={busy}
          variant="contained"
          data-automation-id="first-contract-connect-all"
        >
          {t('firstContractDialog.ConnectAll')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
